/* eslint-disable max-lines -- legacy size; refactor planned in plans/cached-singing-fiddle.md */
import { defineMutator } from "@rocicorp/zero";
import { z } from "zod";
import { zql } from "../schema";
import { FULL_MEMBER_ROLES } from "../constants";
// eslint-disable-next-line no-restricted-imports -- legacy junk-drawer file; rename in plans/cached-singing-fiddle.md
import { requireWorkspaceRole } from "./helpers";
import { makeWorkspaceId, makeUserId } from "../types/branded-ids";
import {
  validateFieldDefinitions,
  validateItemFields,
  validatePrefix,
  type DefaultSort,
  type FieldDefinition,
  type FieldValue,
} from "../schema/list-field-types";

export const listsMutators = {
  insert: defineMutator(
    z.object({
      id: z.string(),
      workspace_id: z.string(),
      name: z.string(),
      description: z.string().optional().nullable(),
      icon: z.string().optional().nullable(),
      color: z.string().optional().nullable(),
      prefix: z.string().optional(),
      template_type: z.string().optional(),
      field_definitions: z.any(),
      default_sort: z.any().optional().nullable(),
      default_group_by: z.string().optional().nullable(),
      created_by: z.string(),
      created_at: z.number(),
      updated_at: z.number(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.created_by !== ctx.sub) {
        throw new Error("Unauthorized: created_by mismatch");
      }
      if (tx.location === "server") {
        await requireWorkspaceRole(
          tx,
          makeWorkspaceId(args.workspace_id),
          makeUserId(ctx.sub),
          [...FULL_MEMBER_ROLES],
        );
      }
      const fieldDefs = args.field_definitions as FieldDefinition[];
      const err = validateFieldDefinitions(fieldDefs);
      if (err) throw new Error(err);

      const prefix = args.prefix ?? null;
      if (prefix !== null) {
        const prefixErr = validatePrefix(prefix);
        if (prefixErr) throw new Error(prefixErr);
      }

      // Validate default_sort shape and fieldId
      let validatedSort = args.default_sort;
      if (validatedSort !== undefined && validatedSort !== null) {
        if (
          typeof validatedSort?.fieldId === "string" &&
          (validatedSort?.direction === "asc" ||
            validatedSort?.direction === "desc") &&
          fieldDefs.some((d) => d.id === validatedSort.fieldId)
        ) {
          validatedSort = {
            fieldId: validatedSort.fieldId,
            direction: validatedSort.direction,
          };
        } else {
          validatedSort = null;
        }
      }

      // Validate default_group_by references a select/user field
      let validatedGroupBy = args.default_group_by;
      if (validatedGroupBy) {
        const groupField = fieldDefs.find((d) => d.id === validatedGroupBy);
        if (
          !groupField ||
          (groupField.type !== "select" && groupField.type !== "user")
        ) {
          validatedGroupBy = null;
        }
      }

      await tx.mutate.lists.insert({
        ...args,
        prefix,
        template_type: args.template_type ?? "default",
        field_definitions: fieldDefs,
        default_sort: validatedSort,
        default_group_by: validatedGroupBy,
      });
    },
  ),
  update: defineMutator(
    z.object({
      id: z.string(),
      name: z.string().optional(),
      description: z.string().optional().nullable(),
      icon: z.string().optional().nullable(),
      color: z.string().optional().nullable(),
      prefix: z.string().optional().nullable(),
      field_definitions: z.any().optional(),
      default_sort: z.any().optional().nullable(),
      default_group_by: z.string().optional().nullable(),
      updated_at: z.number().optional(),
    }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const list = await tx.run(zql.lists.where("id", args.id).one());
        if (!list) return;
        if (list.created_by !== ctx.sub) {
          await requireWorkspaceRole(
            tx,
            makeWorkspaceId(list.workspace_id),
            makeUserId(ctx.sub),
          );
        }
      }
      const now = args.updated_at ?? Date.now();

      if (args.prefix !== undefined && args.prefix !== null) {
        const prefixErr = validatePrefix(args.prefix);
        if (prefixErr) throw new Error(prefixErr);
      }

      // Context-dependent validation requires tx.query (available on server push)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const txQuery = (tx as any).query as typeof zql | undefined;
      if (txQuery && args.field_definitions !== undefined) {
        const newDefs = args.field_definitions as FieldDefinition[];
        const err = validateFieldDefinitions(newDefs);
        if (err) throw new Error(err);

        // Load existing list to enforce immutability rules
        const existing = await txQuery.lists.where("id", args.id).one().run();
        if (!existing) throw new Error("List not found");

        const oldDefs = existing.field_definitions as FieldDefinition[];
        const oldDefMap = new Map(oldDefs.map((d) => [d.id, d]));

        for (const newDef of newDefs) {
          const oldDef = oldDefMap.get(newDef.id);
          if (oldDef) {
            // Type immutability
            if (oldDef.type !== newDef.type) {
              throw new Error(
                `Cannot change type of field "${newDef.id}" from "${oldDef.type}" to "${newDef.type}"`,
              );
            }
            // Select/multiselect option ID immutability
            if (oldDef.type === "select" || oldDef.type === "multiselect") {
              const oldOptionIds = new Set(
                (oldDef.config?.options ?? []).map((o) => o.id),
              );
              const newOptions = newDef.config?.options ?? [];
              for (const oldOptId of oldOptionIds) {
                if (!newOptions.some((o) => o.id === oldOptId)) {
                  throw new Error(
                    `Cannot remove option "${oldOptId}" from field "${newDef.id}"`,
                  );
                }
              }
            }
          }
        }

        // Check for new required fields on non-empty list
        const items = await txQuery.list_items.where("list_id", args.id).run();
        if (items.length > 0) {
          for (const newDef of newDefs) {
            const oldDef = oldDefMap.get(newDef.id);
            if (
              !oldDef &&
              newDef.required &&
              newDef.config?.defaultValue === undefined
            ) {
              throw new Error(
                `Cannot add required field "${newDef.id}" without a defaultValue when list has items`,
              );
            }
          }
        }
      } else if (args.field_definitions !== undefined) {
        // Client-side optimistic: validate structure only
        const newDefs = args.field_definitions as FieldDefinition[];
        const err = validateFieldDefinitions(newDefs);
        if (err) throw new Error(err);
      }

      // Resolve field definitions for default_sort / default_group_by validation
      let resolvedDefs: FieldDefinition[] | undefined;
      if (
        args.default_sort !== undefined ||
        args.default_group_by !== undefined
      ) {
        if (args.field_definitions !== undefined) {
          resolvedDefs = args.field_definitions as FieldDefinition[];
        } else if (txQuery) {
          const existing = await txQuery.lists.where("id", args.id).one().run();
          if (existing) {
            resolvedDefs = existing.field_definitions as FieldDefinition[];
          }
        }
      }

      // Build typed update payload
      const updatePayload: {
        id: string;
        updated_at: number;
        name?: string;
        description?: string | null;
        icon?: string | null;
        color?: string | null;
        field_definitions?: FieldDefinition[];
        default_sort?: DefaultSort;
        default_group_by?: string | null;
        prefix?: string | null;
      } = {
        id: args.id,
        updated_at: now,
      };
      if (args.prefix !== undefined) updatePayload.prefix = args.prefix;
      if (args.name !== undefined) updatePayload.name = args.name;
      if (args.description !== undefined)
        updatePayload.description = args.description;
      if (args.icon !== undefined) updatePayload.icon = args.icon;
      if (args.color !== undefined) updatePayload.color = args.color;
      if (args.field_definitions !== undefined)
        updatePayload.field_definitions =
          args.field_definitions as FieldDefinition[];

      // Validate and set default_sort
      if (args.default_sort !== undefined) {
        if (args.default_sort === null) {
          updatePayload.default_sort = null;
        } else if (
          typeof args.default_sort?.fieldId === "string" &&
          (args.default_sort?.direction === "asc" ||
            args.default_sort?.direction === "desc")
        ) {
          // Shape is valid — check fieldId exists in definitions
          if (
            resolvedDefs &&
            !resolvedDefs.some((d) => d.id === args.default_sort.fieldId)
          ) {
            updatePayload.default_sort = null;
          } else {
            updatePayload.default_sort = {
              fieldId: args.default_sort.fieldId,
              direction: args.default_sort.direction,
            };
          }
        } else {
          // Invalid shape — clear to null
          updatePayload.default_sort = null;
        }
      }

      // Validate and set default_group_by
      if (args.default_group_by !== undefined) {
        if (args.default_group_by && resolvedDefs) {
          const groupField = resolvedDefs.find(
            (d) => d.id === args.default_group_by,
          );
          if (
            !groupField ||
            (groupField.type !== "select" && groupField.type !== "user")
          ) {
            updatePayload.default_group_by = null;
          } else {
            updatePayload.default_group_by = args.default_group_by;
          }
        } else {
          updatePayload.default_group_by = args.default_group_by;
        }
      }

      await tx.mutate.lists.update(updatePayload);
    },
  ),
  delete: defineMutator(
    z.object({ id: z.string() }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const list = await tx.run(zql.lists.where("id", args.id).one());
        if (!list) return;
        if (list.created_by !== ctx.sub) {
          await requireWorkspaceRole(
            tx,
            makeWorkspaceId(list.workspace_id),
            makeUserId(ctx.sub),
          );
        }
      }
      await tx.mutate.lists.delete(args);
    },
  ),
};

export const listItemsMutators = {
  insert: defineMutator(
    z.object({
      id: z.string(),
      list_id: z.string(),
      fields: z.any(),
      position: z.number(),
      sequence_number: z.number().optional(),
      is_archived: z.boolean().optional(),
      created_by: z.string(),
      updated_by: z.string().optional(),
      created_at: z.number(),
      updated_at: z.number(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.created_by !== ctx.sub) {
        throw new Error("Unauthorized: created_by mismatch");
      }
      if (tx.location === "server") {
        const list = await tx.run(zql.lists.where("id", args.list_id).one());
        if (!list) throw new Error("List not found");
        await requireWorkspaceRole(
          tx,
          makeWorkspaceId(list.workspace_id),
          makeUserId(ctx.sub),
          [...FULL_MEMBER_ROLES],
        );
      }
      const fields = {
        ...(args.fields as Record<string, FieldValue>),
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const txQuery = (tx as any).query as typeof zql | undefined;
      if (txQuery) {
        // Server push: validate against field definitions
        const list = await txQuery.lists.where("id", args.list_id).one().run();
        if (!list) throw new Error("List not found");

        const fieldDefs = list.field_definitions as FieldDefinition[];

        // Apply defaults for missing fields
        for (const def of fieldDefs) {
          if (!(def.id in fields) && def.config?.defaultValue !== undefined) {
            fields[def.id] = def.config.defaultValue;
          }
        }

        // Strip keys not in field definitions
        const validIds = new Set(fieldDefs.map((d) => d.id));
        for (const key of Object.keys(fields)) {
          if (!validIds.has(key)) {
            delete fields[key];
          }
        }

        // Validate field values against definitions
        const fieldErr = validateItemFields(fields, fieldDefs);
        if (fieldErr) throw new Error(fieldErr);
      }

      await tx.mutate.list_items.insert({
        ...args,
        fields,
        is_archived: args.is_archived ?? false,
      });
    },
  ),
  update: defineMutator(
    z.object({
      id: z.string(),
      fields: z.any().optional(),
      position: z.number().optional(),
      sequence_number: z.number().optional(),
      is_archived: z.boolean().optional(),
      updated_by: z.string().optional(),
      updated_at: z.number().optional(),
    }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const item = await tx.run(zql.list_items.where("id", args.id).one());
        if (!item) return;
        const list = await tx.run(zql.lists.where("id", item.list_id).one());
        if (!list) return;
        await requireWorkspaceRole(
          tx,
          makeWorkspaceId(list.workspace_id),
          makeUserId(ctx.sub),
          [...FULL_MEMBER_ROLES],
        );
      }
      const now = args.updated_at ?? Date.now();

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const txQuery = (tx as any).query as typeof zql | undefined;
      if (args.fields !== undefined && txQuery) {
        // Server push: load, merge, validate
        const existing = await txQuery.list_items
          .where("id", args.id)
          .one()
          .run();
        if (!existing) throw new Error("List item not found");

        const list = await txQuery.lists
          .where("id", existing.list_id)
          .one()
          .run();
        if (!list) throw new Error("List not found");

        const fieldDefs = list.field_definitions as FieldDefinition[];
        const existingFields = (existing.fields ?? {}) as Record<
          string,
          FieldValue
        >;
        const patch = args.fields as Record<string, FieldValue>;

        // Merge patch into existing fields by top-level field ID
        const merged = { ...existingFields, ...patch };

        // Strip keys not in current field definitions
        const validIds = new Set(fieldDefs.map((d) => d.id));
        for (const key of Object.keys(merged)) {
          if (!validIds.has(key)) {
            delete merged[key];
          }
        }

        // Apply defaults for required fields with defaultValue that are missing
        for (const def of fieldDefs) {
          if (
            def.required &&
            def.config?.defaultValue !== undefined &&
            !(def.id in merged)
          ) {
            merged[def.id] = def.config.defaultValue;
          }
        }

        // Validate merged field values against definitions
        const fieldErr = validateItemFields(merged, fieldDefs);
        if (fieldErr) throw new Error(fieldErr);

        await tx.mutate.list_items.update({
          id: args.id,
          fields: merged,
          position: args.position,
          sequence_number: args.sequence_number,
          is_archived: args.is_archived,
          updated_by: args.updated_by,
          updated_at: now,
        });
      } else {
        // Client-side optimistic or non-fields update
        await tx.mutate.list_items.update({
          id: args.id,
          fields: args.fields,
          position: args.position,
          sequence_number: args.sequence_number,
          is_archived: args.is_archived,
          updated_by: args.updated_by,
          updated_at: now,
        });
      }
    },
  ),
  delete: defineMutator(
    z.object({ id: z.string() }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const item = await tx.run(zql.list_items.where("id", args.id).one());
        if (!item) return;
        const list = await tx.run(zql.lists.where("id", item.list_id).one());
        if (!list) return;
        if (item.created_by !== ctx.sub) {
          // Creator OR workspace admin (moderation).
          await requireWorkspaceRole(
            tx,
            makeWorkspaceId(list.workspace_id),
            makeUserId(ctx.sub),
          );
        }
      }
      await tx.mutate.list_items.delete(args);
    },
  ),
};

export const listItemCommentsMutators = {
  insert: defineMutator(
    z.object({
      id: z.string(),
      list_item_id: z.string(),
      created_by: z.string(),
      body: z.string(),
      created_at: z.number(),
      modified_at: z.number(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.created_by !== ctx.sub) {
        throw new Error("Unauthorized: created_by mismatch");
      }
      if (tx.location === "server") {
        const item = await tx.run(
          zql.list_items.where("id", args.list_item_id).one(),
        );
        if (!item) throw new Error("List item not found");
        const list = await tx.run(zql.lists.where("id", item.list_id).one());
        if (!list) throw new Error("List not found");
        await requireWorkspaceRole(
          tx,
          makeWorkspaceId(list.workspace_id),
          makeUserId(ctx.sub),
          [...FULL_MEMBER_ROLES],
        );
      }
      await tx.mutate.list_item_comments.insert(args);
    },
  ),
  update: defineMutator(
    z.object({
      id: z.string(),
      body: z.string().optional(),
      modified_at: z.number().optional(),
    }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const comment = await tx.run(
          zql.list_item_comments.where("id", args.id).one(),
        );
        if (!comment) return;
        if (comment.created_by !== ctx.sub) {
          throw new Error("Unauthorized: can only edit own comments");
        }
      }
      await tx.mutate.list_item_comments.update({
        ...args,
        modified_at: args.modified_at ?? Date.now(),
      });
    },
  ),
  delete: defineMutator(
    z.object({ id: z.string() }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const comment = await tx.run(
          zql.list_item_comments.where("id", args.id).one(),
        );
        if (!comment) return;
        if (comment.created_by !== ctx.sub) {
          // Workspace admin/owner can also delete (moderation)
          const item = await tx.run(
            zql.list_items.where("id", comment.list_item_id).one(),
          );
          if (!item) return;
          const list = await tx.run(zql.lists.where("id", item.list_id).one());
          if (!list) return;
          await requireWorkspaceRole(
            tx,
            makeWorkspaceId(list.workspace_id),
            makeUserId(ctx.sub),
          );
        }
      }
      await tx.mutate.list_item_comments.delete(args);
    },
  ),
};
